/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraFoundationNode,
  GamoraFoundationNodeArgs,
  GamoraFoundationNodeChildMap,
  GamoraFoundationNodeRoot,
} from '../node/node.js';
import { GamoraFoundationTask, GamoraFoundationTaskInput } from './task.js';
import { assert } from '../../utils/assert.js';

/**
 * Tasks type
 * @public
 */
export type GamoraFoundationTaskManagerTasks = {
  [namespace: string]: GamoraFoundationTask<GamoraFoundationTaskInput>[];
};

/**
 * Task manager
 * @public
 */
export class GamoraFoundationTaskManager<
  TChildMap extends GamoraFoundationNodeChildMap<TChildMap>,
  TRoot extends GamoraFoundationNodeRoot = any,
  TSuper extends GamoraFoundationNodeRoot = any,
> extends GamoraFoundationNode<TChildMap, TRoot, TSuper> {
  protected _$tasks: GamoraFoundationTaskManagerTasks = {};

  constructor(args: GamoraFoundationNodeArgs<TChildMap>) {
    super(args);
  }

  /**
   * Register the task
   * Manager tracks the lifecycle of the task
   */
  $registerTask<T extends GamoraFoundationTask<GamoraFoundationTaskInput>>(
    task: T,
  ): void {
    // Get parent uuid
    const { $namespace } = task;

    // Add new entry for namespace if not exists already
    if (!this._$tasks[$namespace]) {
      this._$tasks[$namespace] = [];
    }

    // Find inedx of the task to be unique
    const indexToUnique = this._$tasks[$namespace].findIndex(
      (registeredTask) => registeredTask.$name === task.$name,
    );
    assert(indexToUnique === -1, 'Invalid task: task name already registered.');

    // Add task againts paranet uuid
    this._$tasks[$namespace].push(task);
  }

  /**
   * Unregisters task
   * Manager no longer tracks the lifecycle of the task
   */
  $unregisterTask<T extends GamoraFoundationTask<GamoraFoundationTaskInput>>(
    task: T,
  ): void {
    // Get parent uuid
    const { $namespace } = task;

    // Get parent tasks
    const tasksByNamespace = this._$tasks[$namespace];
    assert(
      tasksByNamespace !== undefined,
      'Invalid task: task namespace does not exist.',
    );

    // Find inedx of the task to be removed
    const indexToRemove = tasksByNamespace.findIndex(
      (registeredTask) => registeredTask.$name === task.$name,
    );
    assert(indexToRemove !== -1, 'Invalid task: task name does not exist.');

    // Remove task if exists
    tasksByNamespace.splice(indexToRemove, 1);
  }

  /**
   * Get a task in the manager
   */
  $getTask<T extends GamoraFoundationTask<GamoraFoundationTaskInput>>(
    namespace: string,
    name: string,
  ): T {
    // Get parent tasks
    const tasksByNamespace = this._$tasks[namespace];
    assert(
      tasksByNamespace !== undefined,
      'Invalid namespace: namespace does not exist.',
    );

    // Find task
    const task = tasksByNamespace.find((task) => task.$name === name);
    assert(task !== undefined, 'Invalid name: name does not exist.');

    return task as T;
  }

  /**
   * Has a task in the manager
   */
  $hasTask(namespace: string, name: string): boolean {
    const tasksByNamespace = this._$tasks[namespace];
    if (!tasksByNamespace) {
      return false;
    }

    // Find task
    const task = tasksByNamespace.find((task) => task.$name === name);
    if (!task) {
      return false;
    }

    return true;
  }

  /**
   * Start all the tasks registered for namespace
   */
  async $startTasks(namespace: string): Promise<void> {
    // Get all the tasks registered
    const tasksByNamespace = this._$tasks[namespace];
    assert(
      tasksByNamespace !== undefined,
      'Invalid namespace: namespace does not exist.',
    );

    // Ensure that tasks are not running at this moment
    assert(
      tasksByNamespace.every((task) => {
        return !task.$isRunning;
      }),
    );

    // Start each task
    await Promise.allSettled(tasksByNamespace.map((task) => task.$start()));
  }

  /**
   * Terminate all the tasks registered for namespace
   */
  async $terminateTasks(namespace: string): Promise<void> {
    // Get all the tasks registered
    const tasksByNamespace = this._$tasks[namespace];
    assert(
      tasksByNamespace !== undefined,
      'Invalid namespace: namespace does not exist.',
    );

    // Ensure that tasks are running at this moment
    assert(
      tasksByNamespace.every((task) => {
        return task.$isRunning;
      }),
    );

    // Terminate each task
    await Promise.allSettled(tasksByNamespace.map((task) => task.$terminate()));
  }

  /**
   * Terminate all the tasks registered for namespace
   */
  async $terminateAndUnregisterTasks(namespace: string): Promise<void> {
    // Get all the tasks registered
    const tasksByNamespace = this._$tasks[namespace];
    assert(
      tasksByNamespace !== undefined,
      'Invalid namespace: namespace does not exist.',
    );

    // Terminate all tasks in this namespace
    await this.$terminateTasks(namespace);

    // Unregister all tasks in this namespace
    delete this._$tasks[namespace];
  }

  /**
   * Terminate all the tasks registered
   */
  async $terminateAndUnregisterAllTasks(): Promise<void> {
    // Get all the namespaces
    const namespaces = Object.keys(this._$tasks);

    // Terminate and unregister tasks registered for each namespace
    await Promise.allSettled(
      namespaces.map((namespace) =>
        this.$terminateAndUnregisterTasks(namespace),
      ),
    );
  }
}
